const { jsonToGraphQLQuery } = require('json-to-graphql-query');
const { introspectionApi, queryApi, queryGraphql, queryHelper } = require('da-graphql-client/web');
const { merge } = require('lodash');
const { camelCase } = require('change-case');
const { QueryBuilderAttributeTypes } = require('./operators');
const { filterAttributes } = require('./helper');
const { identifyMissingAttributeEnums, setAttributeEnums } = require('./enum');
const { queryBuilderObjectToJsonToGraphObject } = require('./translate');
const { consolidateAttributes, unconsolidateAttributes } = require('./consolidate');

const AsyncLock = require('async-lock');
const {
    addAttributes,
    sortAttributes,
    removeUnresolvedObjectPendingAttributes,
    addLookupValueForARelationshipAttribute,
} = require('./attribute');
const {
    fetchRootTypeNames,
    fetchTypes,
    getCacheKey,
    getFetchTypeName,
    getMaxVersionForBaseName,
    getMaxVersionNameForBaseName,
    SchemaTypes,
} = introspectionApi;
const { buildResults, getDefaultFilter } = queryApi;
const lock = new AsyncLock();

/**
 * Fetch EEDM model attributes and all child attributes recursively.
 */
const fetchAttributes = async (api, graphType, filterText = null, relationshipAttribute, fetchSelectSchema) => {
    // only want to query for max base version
    const schemaType = fetchSelectSchema ? SchemaTypes.Select : SchemaTypes.Filter;
    graphType = fetchSelectSchema ? graphType : getFetchTypeName(graphType);
    graphType = await getMaxVersionNameForBaseName(api, graphType, schemaType);

    const cacheKey = getCacheKey(api, graphType);

    // if we have a child model via relationship, pull that graphType
    if (relationshipAttribute) {
        graphType = relationshipAttribute.graphQLTypeName;
    }

    // don't want to call fetchAllAttributes multiple times because it's costly, so mutex
    const result = await lock.acquire(cacheKey, async (done) => {
        try {
            let attributes = await fetchAllAttributes(
                api, graphType, relationshipAttribute, schemaType);
            // filter the attributes by filterText
            attributes = filterAttributes(attributes, filterText);
            done(null, attributes);
            return;
        } catch (error) {
            done(error, null);
        }
    });
    return result;
};

const fetchAllAttributes = async (api, graphType, relationshipAttribute = null, schemaType = SchemaTypes.filter) => {
    // step 1: get root types so we know where to stop recursive introspection
    let rootTypeNames = await fetchRootTypeNames(api);

    // step 2: recursively query types at each level
    let typesToFetch = [graphType];
    const attributes = [];
    do {
        let types = await fetchTypes(api, typesToFetch);
        const originalAttributes = [...attributes];
        for (const type of types) {
            addAttributes(attributes, type, rootTypeNames, relationshipAttribute, schemaType);
        }

        // If there are any original attributes still having ObjectPending, then they need to
        // be removed because the type could not be located.  Otherwise, we recurse forever.
        removeUnresolvedObjectPendingAttributes(originalAttributes, attributes);

        // if we have any more objects pending, recurse to load those types
        typesToFetch = attributes
            .filter((a) => a.type === QueryBuilderAttributeTypes.ObjectPending)
            .map((a) => a.graphQLTypeName);

    } while (typesToFetch.length > 0);

    sortAttributes(attributes);
    // Add the lookup attribute for the relationship attribute
    if (relationshipAttribute) {
        addLookupValueForARelationshipAttribute(attributes, relationshipAttribute);
    }
    consolidateAttributes(attributes)
    return attributes;
};

/**
 * Fetch related attributes after selecting a base attribute. Usually needed to populate static enum values.
 */
const populateAttributesAfterSelection = async (api, graphType, where) => {
    const results = await fetchAttributes(api, graphType);
    const { attributesToPopulate, graphQLTypeNames } = identifyMissingAttributeEnums(where, results);

    if (!graphQLTypeNames.length) { return; }
    const graphTypes = await fetchTypes(api, graphQLTypeNames);
    setAttributeEnums(attributesToPopulate, graphTypes);
};

/**
 * Fetch EEDM items from data access GraphQL api via query from query builder for given graphType.
 */
const fetchEedmValues = async (
    api,
    graphType,
    query,
    attributes = [],
    sortAndPageOptions,
    filterText,
    filterAttribute,
    includeTotalCount = false,
    customSort = false,
    schemaType = SchemaTypes.Filter) => {

    if (!graphType) {
        return queryHelper.emptyListResponse();
    }
    const version = await getMaxVersionForBaseName(api, getFetchTypeName(graphType), schemaType);
    graphType = camelCase(`${graphType}${version}`);

    const defaultQuery = getDefaultFilter(graphType, attributes, sortAndPageOptions.orderBy, filterText, filterAttribute, includeTotalCount);
    if (customSort && sortAndPageOptions.orderBy) {
        defaultQuery.query[graphType].__args.sort = [sortAndPageOptions.orderBy];
    }
    const queryBuilderOutput = {
        groups: query.groups || [],
        version: query.version,
        where: unconsolidateAttributes(query.where) || []
    };
    let queryBuilderQuery = merge({}, defaultQuery.query,
        queryBuilderObjectToJsonToGraphObject(graphType, queryBuilderOutput));

    const myQuery = jsonToGraphQLQuery({ query: queryBuilderQuery });
    const variables = queryHelper.getCriteriaVariablesRelay(sortAndPageOptions);
    if (filterText && filterText.trim() && filterAttribute) {
        variables.contains = filterText;
    }
    try {
        const response = await queryGraphql({
            api: api,
            query: myQuery,
            variables,
        });
        const { data, errors } = queryHelper.response(response);
        if (errors) {
            throw errors;
        }
        const results = queryHelper.relayToListResponse(data[graphType]);
        return buildResults(results, attributes);
    } catch (error) {
        throw error;
    }
};

module.exports = {
    fetchAttributes,
    fetchEedmValues,
    populateAttributesAfterSelection,
};
